import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, TrendingUp, TrendingDown, Radio, Loader2, Sparkles, Zap } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import TradeNowDialog from "@/components/TradeNowDialog";
import { useValidationStatus } from "@/components/Validation";
import { useTradingStyle } from "@/contexts/TradingStyleContext";
import { api } from "@/lib/api";

const REGIME_CLS = {
  STRONG_RISK_ON: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  RISK_ON: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  NEUTRAL: "text-slate-300 border-slate-500/30 bg-slate-500/10",
  RISK_OFF: "text-red-300 border-red-500/30 bg-red-500/10",
  STRONG_RISK_OFF: "text-red-300 border-red-500/40 bg-red-500/10",
};

function fmt(v, d = 2) { return v == null ? "—" : Number(v).toFixed(d); }

function OppCard({ row, side, testid, descriptive }) {
  const nav = useNavigate();
  const score = side === "long" ? row.long_score : row.short_score;
  const isLong = side === "long";
  return (
    <div
      data-testid={testid}
      onClick={() => nav(`/stock/${row.ticker}`)}
      className={`border ${isLong ? "border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10" : "border-red-500/30 bg-red-500/5 hover:bg-red-500/10"} px-3 py-2.5 cursor-pointer transition-colors`}
    >
      <div className="flex items-baseline justify-between">
        <span className="mono text-lg font-extrabold text-slate-100">{row.ticker}</span>
        <span className={`mono text-lg font-bold tabular-nums ${isLong ? "text-emerald-300" : "text-red-300"}`}>{fmt(score, 1)}</span>
      </div>
      <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5 truncate">
        {row.strength_band}
        {descriptive
          ? <> · <span title="Descriptive statistic only — see Analytics validation status.">desc</span></>
          : <> · Conf {row.confidence || "—"}</>}
        {" · R/R "}{fmt(row.risk_reward_t1, 2)}
      </div>
      <div className="mono text-[10px] text-slate-400 mt-1 truncate">
        Zone ${fmt(row.entry_zone?.low)} – ${fmt(row.entry_zone?.high)} · Stop ${fmt(row.stop_loss)}
      </div>
      <div className="mono text-[10px] text-cyan-300 mt-1 truncate">▸ {row.top_factor || "—"}</div>
    </div>
  );
}

export default function Dashboard() {
  const [openSearch, setOpenSearch] = useState(false);
  const [openTradeNow, setOpenTradeNow] = useState(false);
  const validation = useValidationStatus();
  const swingDescriptive = validation?.swing?.verdict === "MARGINAL" || validation?.swing?.verdict === "NULL";
  const { style } = useTradingStyle() || {};
  const isIntraday = style === "INTRADAY";
  const [opps, setOpps] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpenSearch(true); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/signal/opportunities", { params: { top_n: 6 } })
      .then((r) => setOpps(r.data))
      .catch(() => setOpps(null))
      .finally(() => setLoading(false));
  }, []);

  const regime = opps?.regime || {};
  const rCls = REGIME_CLS[regime.regime] || REGIME_CLS.NEUTRAL;

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">

          {/* Regime banner */}
          <div data-testid="dashboard-regime-banner" className="border border-[#1E293B] bg-[#0F1626] relative overflow-hidden mb-3">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
            <div className="p-4 flex flex-wrap items-center gap-4">
              <Radio className="h-4 w-4 text-cyan-400" />
              <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Market Regime</span>
              <span className={`mono text-sm px-2 py-1 border ${rCls} uppercase tracking-wider`} data-testid="dashboard-regime-chip">{regime.regime || "…"}</span>
              <span className="mono text-[11px] text-slate-500">Score {fmt(regime.score, 1)}</span>
              <span className="mono text-[11px] text-slate-500">
                {regime.character?.trending ? "TRENDING" : "RANGE"} · {regime.character?.high_vol ? "ELEVATED VOL" : "NORMAL VOL"}
              </span>
              <div className="ml-auto flex items-center gap-3">
                {["SPY", "QQQ", "IWM"].map((k) => (
                  <span key={k} className="mono text-[11px] text-slate-400">{k} <span className="text-slate-100">{fmt(regime.per_index?.[k]?.score, 0)}</span></span>
                ))}
              </div>
            </div>
          </div>

          {/* Ticker search prompt + Trade Now (INTRADAY mode) */}
          <div className="mb-3 flex items-center gap-3 flex-wrap">
            <button
              data-testid="dashboard-open-search"
              onClick={() => setOpenSearch(true)}
              className="group flex items-center gap-2 h-10 px-3 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 mono text-xs uppercase tracking-widest text-cyan-200 transition-colors"
            >
              <Search className="h-3.5 w-3.5" /> Search ticker
              <span className="flex gap-1 ml-2">
                <kbd className="px-1.5 py-0.5 border border-cyan-500/40 text-[10px] text-cyan-300 mono">⌘</kbd>
                <kbd className="px-1.5 py-0.5 border border-cyan-500/40 text-[10px] text-cyan-300 mono">K</kbd>
              </span>
            </button>
            {isIntraday && (
              <button
                data-testid="dashboard-trade-now"
                onClick={() => setOpenTradeNow(true)}
                className="flex items-center gap-2 h-10 px-4 border border-amber-500/60 bg-amber-500/15 hover:bg-amber-500/25 mono text-xs uppercase tracking-widest font-bold text-amber-200 transition-colors"
              >
                <Zap className="h-4 w-4" />
                Trade Now
              </button>
            )}
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500">Universe: {opps?.universe_size ?? "—"} tickers · {opps?.cache === "hit" ? "cached" : "fresh"}</span>
          </div>

          {loading && (
            <div className="p-8 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Ranking opportunities…
            </div>
          )}

          {!loading && opps && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Top Long */}
              <div className="border border-emerald-500/20 bg-[#0F1626]" data-testid="top-long-list">
                <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="mono text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Top LONG Opportunities</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-600 ml-auto">Ranked by score · confidence · R/R</span>
                </div>
                <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {(opps.top_long || []).length === 0 && (
                    <div className="mono text-[11px] text-slate-500 text-center py-4 col-span-full">No LONG signals in this regime.</div>
                  )}
                  {(opps.top_long || []).map((r) => (
                    <OppCard key={r.ticker} row={r} side="long" testid={`opp-long-${r.ticker}`} descriptive={swingDescriptive} />
                  ))}
                </div>
              </div>

              {/* Top Short */}
              <div className="border border-red-500/20 bg-[#0F1626]" data-testid="top-short-list">
                <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
                  <TrendingDown className="h-3.5 w-3.5 text-red-400" />
                  <span className="mono text-[10px] uppercase tracking-widest text-red-300 font-semibold">Top SHORT Opportunities</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-600 ml-auto">Never mixed with LONG</span>
                </div>
                <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {(opps.top_short || []).length === 0 && (
                    <div className="mono text-[11px] text-slate-500 text-center py-4 col-span-full">No SHORT signals in this regime.</div>
                  )}
                  {(opps.top_short || []).map((r) => (
                    <OppCard key={r.ticker} row={r} side="short" testid={`opp-short-${r.ticker}`} descriptive={swingDescriptive} />
                  ))}
                </div>
              </div>

              {/* Empty state */}
              {opps.no_qualifying_signals && (
                <div className="col-span-full border border-amber-500/30 bg-amber-500/10 px-4 py-3 mono text-xs text-amber-200" data-testid="dashboard-no-signals">
                  No directional signals qualify right now. Highest-ranked WAIT setups:
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {(opps.top_ranked_wait || []).map((r) => (
                      <button key={r.ticker} onClick={() => navigate(`/stock/${r.ticker}`)} className="border border-[#1E293B] bg-[#0F1626] hover:bg-[#172036] px-2 py-1.5 text-left">
                        <div className="mono text-sm font-bold text-slate-100">{r.ticker}</div>
                        <div className="mono text-[10px] text-slate-500">L {fmt(r.long_score, 0)} / S {fmt(r.short_score, 0)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <TradeNowDialog open={openTradeNow} onOpenChange={setOpenTradeNow} />
      <DisclaimerFooter />
    </div>
  );
}
